import { useMemo, useState } from "react";
import { PageHeader } from "../components/Layout";
import { LineChart } from "../components/Chart";
import { InfoTip } from "../components/Erklaerung";
import { Card, Badge, KlasseBadge } from "../components/ui";
import { VertragsgrundlageHinweis, VertragsgrundlageTabelle, VertragsgrundlageZuruecksetzen } from "../components/Vertragsgrundlage";
import { useApp } from "../../store/AppStore";
import { prognoseJeJahr } from "../../domain/analysis";
import { useAssignedLandscapes } from "../../store/cutoverPlan";
import { baselineComponents, type BaselinePersonRow } from "../../domain/baseline";
import { ChevronDown, ChevronRight, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { formatFue, formatNum, formatWeightExact } from "../../domain/rules";
import type { LicenseClass, SourceState } from "../../domain/types";

const KEY_LABEL: Record<string, string> = { username: "Benutzername", personalnummer: "Personalnummer", email: "E-Mail", custom: "freies Feld" };

// ------------------------------------------------------------ Bedarfsaufbau (Baseline-Validierung)
const TYPE_LABEL: Record<string, string> = { lager: "Lager", buero: "Büro", it: "IT", entwickler: "Entwickler", extern: "Extern", technisch: "Technisch" };

function BaselinePersonen({ rows }: { rows: BaselinePersonRow[] }) {
  const [n, setN] = useState(100);
  if (rows.length === 0) return <div className="text-faint text-xs">Keine Person in diesem Baustein.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-faint text-left uppercase tracking-wide">
            <th className="px-2 py-1 font-medium">Person</th><th className="px-2 py-1 font-medium">Name</th><th className="px-2 py-1 font-medium">Typ</th><th className="px-2 py-1 font-medium">Abteilung</th><th className="px-2 py-1 font-medium">Klasse heute → Ziel</th><th className="px-2 py-1 text-right font-medium">Anteil FUE</th><th className="px-2 py-1 text-right font-medium">Anmeldung</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, n).map((r) => (
            <tr key={r.id} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
              <td className="px-2 py-1 font-mono whitespace-nowrap"><Link to={`/berechtigungen?person=${r.id}`} className="hover:underline" style={{ color: "var(--color-brand-600)" }}>{r.id}</Link></td>
              <td className="text-strong px-2 py-1">{r.name ?? "—"}</td>
              <td className="text-dim px-2 py-1">{TYPE_LABEL[r.type] ?? r.type}</td>
              <td className="text-dim px-2 py-1">{r.department ?? r.unit}</td>
              <td className="px-2 py-1"><KlasseBadge cls={r.cls as LicenseClass} />{r.cleanCls !== r.cls && <> → <KlasseBadge cls={r.cleanCls as LicenseClass} /></>}</td>
              <td className="tnum px-2 py-1 text-right">{formatWeightExact(r.fue)}</td>
              <td className="tnum text-dim px-2 py-1 text-right">{r.lastLogonDays === undefined ? "—" : `vor ${formatNum(r.lastLogonDays)} T.`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > n && (
        <div className="mt-1 flex items-center gap-3 text-xs">
          <span className="text-faint">{formatNum(n)} von {formatNum(rows.length)} gezeigt</span>
          <button type="button" onClick={() => setN((x) => x + 200)} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>weitere 200</button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Entitlement-Zustand
const STATE_BADGE: Record<SourceState, { tone: "green" | "amber" | "gray"; label: string }> = {
  angebunden: { tone: "green", label: "angebunden" },
  "in-klaerung": { tone: "amber", label: "in Klärung" },
  offen: { tone: "gray", label: "offen" },
};

export function Vertrag() {
  const { ds, kpis, identityKey, ratio, consolidatedFue, contracted, price } = useApp();
  const prognoseLandscapes = useAssignedLandscapes();
  // Prognose aus der Cutover-Kette (keine Wachstumsfaktoren) — Kurve: kumuliert, nach Optimierung, Vertrag.
  const prognose = prognoseJeJahr(ds, ratio, prognoseLandscapes, contracted, price);
  const prognoseData = prognose.map((j) => ({ label: String(j.jahr), Vertrag: j.vertrag, Bedarf: j.kumuliert, "nach Optimierung": j.nachOptimierung }));
  const prognoseMax = Math.max(contracted, ...prognose.map((j) => j.kumuliert));

  // Bedarfsaufbau: disjunkte, aus den Personen gerechnete Bausteine, Σ = Bedarf heute (I12).
  const keyLabel = KEY_LABEL[identityKey] ?? identityKey;
  const baseline = useMemo(() => baselineComponents(ds, ratio, kpis.ist, keyLabel), [ds, ratio, kpis.ist, keyLabel]);
  const [openStep, setOpenStep] = useState<string | null>(null);
  const check = baseline.components.reduce((a, c) => a + c.fue, 0);
  const checkOk = Math.abs(check - kpis.ist) < 0.5;
  const hi = Math.max(baseline.contract, baseline.total) || 1;
  const delta = baseline.total - baseline.contract;

  return (
    <div>
      <PageHeader route="/vertrag" />

      {/* Vertragsgrundlage je Klasse gegen heute — dieselben Felder und derselbe Store wie in den Einstellungen. */}
      <Card
        title={<span className="inline-flex items-center gap-1">Vertragsgrundlage je Klasse gegen heute <VertragsgrundlageHinweis /></span>}
        subtitle={`Vertragszahl ${formatFue(contracted)} · heute ${formatFue(kpis.ist)} (${keyLabel}) · Summenzeile = Delta des Cockpits`}
        className="mb-6"
        right={
          <span className="inline-flex items-center gap-3">
            <VertragsgrundlageZuruecksetzen />
            <Link to="/einstellungen" className="inline-flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>
              <Settings size={14} /> in den Einstellungen anpassen
            </Link>
          </span>
        }
      >
        <VertragsgrundlageTabelle compact />
      </Card>

      {/* Bedarfsaufbau gegen den Vertrag */}
      <Card
        title={<span className="inline-flex items-center gap-1">Baseline-Validierung <InfoTip>Der heutige Bedarf wird in Bausteine zerlegt, die exakt auf ihn summieren: Grundbedarf, Externe, Karteileichen, gesperrte Personen, Entwickler-Aufschlag, vermeidbare Über-Berechtigung und die Wirkung des Identitätsschlüssels. Jeder Baustein ist aus den Personen gerechnet — Klick auf eine Zeile zeigt sie. Die Vertragszahl {formatFue(baseline.contract)} ist die Summe der Vertragsgrundlage je Klasse — eine Annahme, gegen die alles läuft.</InfoTip></span>}
        subtitle="Vertrag · Bausteine des heutigen Bedarfs · Klick auf eine Zeile zeigt die Personen"
        className="mb-6"
      >
        <div className="space-y-2">
          <div className="-mx-2 rounded-lg px-2 py-1">
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="text-strong font-semibold">Vertrag (Annahme)</span>
              <span className="tnum shrink-0 font-medium" style={{ color: "var(--color-brand-600)" }}>{formatFue(baseline.contract)}</span>
            </div>
            <div className="relative h-5 w-full"><div className="absolute top-0 h-5 rounded" style={{ left: 0, width: `${(baseline.contract / hi) * 100}%`, backgroundColor: "var(--color-brand-600)" }} /></div>
          </div>
          {(() => { let running = 0; return baseline.components.map((c) => {
            const start = running; running += c.fue;
            const open = openStep === c.key;
            const neg = c.fue < 0;
            return (
              <div key={c.key} className="-mx-2 rounded-lg px-2 py-1" style={open ? { backgroundColor: "var(--surface-sunken)" } : undefined}>
                <button type="button" onClick={() => setOpenStep(open ? null : c.key)} className="hover:bg-sunken block w-full rounded text-left" aria-expanded={open}>
                  <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-normal inline-flex items-center gap-1.5">{open ? <ChevronDown size={14} className="text-faint" /> : <ChevronRight size={14} className="text-faint" />}{c.label} <InfoTip>{c.erklaerung}</InfoTip></span>
                    <span className="tnum shrink-0 font-medium" style={{ color: neg ? "var(--color-brand-300)" : "var(--color-brand-400)" }}>{neg ? "−" : "+"}{formatNum(Math.abs(Math.round(c.fue)))}{c.rows.length > 0 && <span className="text-faint ml-2 font-normal">{formatNum(c.rows.length)} Personen</span>}</span>
                  </div>
                  <div className="relative h-5 w-full"><div className="absolute top-0 h-5 rounded" style={{ left: `${(Math.min(start, running) / hi) * 100}%`, width: `${Math.max((Math.abs(c.fue) / hi) * 100, 0.4)}%`, backgroundColor: neg ? "var(--color-brand-300)" : "var(--color-brand-400)" }} /></div>
                </button>
                {open && (
                  <div className="mt-2 rounded-lg p-3 text-sm" style={{ borderLeft: "3px solid var(--color-brand-500)", backgroundColor: "var(--surface-card)" }}>
                    <div className="text-dim mb-2 text-xs">{c.erklaerung}{c.link && <> <Link to={c.link.to} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>→ {c.link.label}</Link></>}</div>
                    {c.rows.length > 0 ? <BaselinePersonen rows={c.rows} /> : <div className="text-faint text-xs">Kein Personenbezug: dieser Baustein ist die Differenz zweier Zählweisen.</div>}
                  </div>
                )}
              </div>
            );
          }); })()}
          <div className="-mx-2 rounded-lg px-2 py-1">
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="text-strong font-semibold">Bedarf heute ({keyLabel})</span>
              <span className="tnum shrink-0 font-medium" style={{ color: "var(--color-amp-red)" }}>{formatFue(baseline.total)} <span className="text-faint ml-2 font-normal">{delta >= 0 ? "+" : "−"}{formatNum(Math.abs(Math.round(delta)))} gegenüber Vertrag</span></span>
            </div>
            <div className="relative h-5 w-full"><div className="absolute top-0 h-5 rounded" style={{ left: 0, width: `${(baseline.total / hi) * 100}%`, backgroundColor: "var(--color-amp-red)" }} /></div>
          </div>
        </div>

        <div className="text-dim mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="inline-flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: checkOk ? "var(--color-amp-green)" : "var(--color-amp-red)" }} />Kontrolle: Σ Bausteine = {formatFue(check)}{checkOk ? " · deckt sich mit Cockpit und Optimierung" : " · Abweichung!"}</span>
          <button type="button" onClick={() => setOpenStep(openStep === "ohne" ? null : "ohne")} className="inline-flex items-center gap-1 hover:underline">{openStep === "ohne" ? <ChevronDown size={12} /> : <ChevronRight size={12} />}nicht in FUE: {formatNum(baseline.ohneFue.persons)} Personen nur auf Systemen mit eigener Metrik (GSP, HCM)</button>
        </div>
        {openStep === "ohne" && <div className="mt-2"><BaselinePersonen rows={baseline.ohneFue.rows} /></div>}
      </Card>

      {/* Entitlement-Register */}
      <Card
        title="Entitlement-Register"
        subtitle={<span className="inline-flex items-center gap-1">FUE · Engines · Digital Access · BTP <InfoTip>Engines (GTS, EWM, TM) bemessen sich nach Volumen: Belege, Lieferpositionen, Transportaufträge. Digital Access und BTP laufen als eigene Metriken. Keine davon wird in FUE umgerechnet.</InfoTip></span>}
        className="mb-6"
      >
        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-faint text-left text-xs uppercase tracking-wide">
                <th className="px-2 py-2 font-medium">Metrik</th>
                <th className="px-2 py-2 text-right font-medium">Vertraglich</th>
                <th className="px-2 py-2 text-right font-medium">Bedarf</th>
                <th className="px-2 py-2 font-medium">Einheit</th>
                <th className="px-2 py-2 font-medium">Zustand</th>
              </tr>
            </thead>
            <tbody>
              {ds.entitlements.map((e) => {
                const isOpen = e.state === "offen";
                const badge = STATE_BADGE[e.state];
                // Bedarf: FUE nutzt den berechneten Ist-Bedarf (real, nicht erfunden);
                // vertraglich ist die Summe der Vertragsgrundlage je Klasse (Store), nicht der Wert aus dem Register.
                const bedarf = e.metric === "FUE" ? kpis.ist : e.required;
                const vertraglich = e.metric === "FUE" ? contracted : e.contracted;
                const rowStyle = isOpen ? { color: "var(--text-faint)" } : undefined;
                return (
                  <tr key={e.metric} className="border-t border-soft align-top" style={rowStyle}>
                    <td className="px-2 py-3">
                      <div className={isOpen ? "font-medium" : "text-normal font-medium"}>{e.label}</div>
                      {e.note && <div className="text-faint mt-0.5 text-xs">{e.note}</div>}
                    </td>
                    <td className="tnum px-2 py-3 text-right">{vertraglich != null ? formatNum(vertraglich) : "—"}</td>
                    <td className="tnum px-2 py-3 text-right">{bedarf != null ? formatNum(bedarf) : "—"}</td>
                    <td className="px-2 py-3">{e.unit}</td>
                    <td className="px-2 py-3"><Badge tone={badge.tone}>{badge.label}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Commitment-Verlauf */}
      <Card
        title="Bedarf entlang der Cutover-Kette je Jahr"
        subtitle={<span className="inline-flex items-center gap-1">Stand am 31.12. · Vertrag flach <InfoTip>Kumulierter Bedarf am Jahresende aus der Cutover-Kette (Reiter Cutover-Gates), nach Optimierung = minus vermeidbare FUE der bis dahin migrierten Systeme. Keine Wachstumsfaktoren. Das letzte Jahr der Kette ist der konsolidierte Gesamtbedarf ({formatFue(consolidatedFue)}).</InfoTip></span>}
      >
        <LineChart
          data={prognoseData}
          yLabel="FUE"
          yMin={0}
          yMax={Math.ceil((prognoseMax * 1.08) / 1000) * 1000}
          series={[
            { key: "Vertrag", color: "var(--color-brand-500)", dashed: true },
            { key: "Bedarf", color: "var(--color-amp-red)" },
            { key: "nach Optimierung", color: "var(--color-amp-green)" },
          ]}
        />
      </Card>
    </div>
  );
}
